import { LngLatBounds } from "maplibre-gl";
import { widenedBounds } from "./searchResultLayers";

export interface SearchHits {
  geojson: GeoJSON.FeatureCollection<GeoJSON.Geometry>;
  // Perceelnamen ("GST00 I 1") — de sleutel waarop de vector-tiles te matchen
  // zijn: hun `id`-property is diezelfde naam, niet het objectID.
  names: string[];
  total: number;
  // Gezet als er niets in beeld staat terwijl het filter elders wél treffers
  // heeft: de aanroeper kan daar dan naartoe zoomen.
  boundsToFit?: LngLatBounds;
}

export interface FilterMeta {
  // Aantal treffers van het hele filter — níet van wat er in beeld staat.
  total: number;
  bounds?: LngLatBounds;
}

const meaningful = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.some(meaningful);
  if (typeof value === "object") return Object.values(value).some(meaningful);
  return true; // getallen en booleans zijn altijd een keuze
};

/**
 * Is er daadwerkelijk ergens op gezocht?
 *
 * Zonder criteria zou de kaart alle percelen van Nederland als treffer tonen:
 * een scherm vol rode stippen dat niets zegt. De zoekpagina stuurt dan een
 * leeg filter (hooguit `query: ""` en lege lijsten), en dat is geen zoekopdracht.
 * Eén verfijning of een ingetypte zoekterm is genoeg.
 */
export function hasCriteria(filter: unknown): boolean {
  if (!filter || typeof filter !== "object") return false;
  return Object.values(filter as Record<string, unknown>).some(meaningful);
}

// Omvang en aantal van álle treffers van een filter, zonder de hits zelf op te
// halen (`hitsPerPage: 0` + het geo_bounds-facet).
export async function fetchFilterMeta(client: any, filter: any): Promise<FilterMeta> {
  const res = await client.search([
    {
      indexName: "reference",
      params: {
        ...filter,
        facets: ["geo_bounds:geometry"],
        attributesToRetrieve: ["geometry"],
        hitsPerPage: 0,
      },
    },
  ]);
  const results = res.results?.[0];
  // Zonder treffers levert de server geen geo_bounds (of een leeg object);
  // een LngLatBounds met NaN's gooit, dus alleen bouwen als de vier zijden er zijn.
  const geo = results?.facets?.geo_bounds;
  const hasBounds =
    geo && [geo.left, geo.top, geo.right, geo.bottom].every((v) => typeof v === "number");
  return {
    total: results?.nbHits ?? 0,
    bounds: hasBounds ? new LngLatBounds([geo.left, geo.top], [geo.right, geo.bottom]) : undefined,
  };
}

// De coördinaten van de treffers van een filter, ongeacht het kaartbeeld.
// Gebruikt om een hele selectie aan een project toe te voegen.
export async function fetchFilterPoints(
  client: any,
  filter: any,
  limit: number
): Promise<[number, number][]> {
  const res = await client.search([
    {
      indexName: "reference",
      params: {
        ...filter,
        facets: [],
        attributesToRetrieve: ["geometry"],
        hitsPerPage: limit,
      },
    },
  ]);
  const hits = res.results?.[0]?.hits || [];
  return hits
    .map((hit: { geometry?: number[] }) => hit.geometry)
    .filter((point?: number[]): point is number[] => Array.isArray(point) && point.length === 2)
    .map((point: number[]) => [point[0], point[1]] as [number, number]);
}

export async function fetchSearchHits(
  client: any,
  filter: any,
  bounds: LngLatBounds
): Promise<SearchHits> {
  const res = await client.search([
    {
      indexName: "reference",
      params: {
        ...filter,
        insideBoundingBox: widenedBounds(bounds).join(","),
        facets: [],
        attributesToRetrieve: ["geometry", "name"],
        hitsPerPage: 10000,
      },
    },
  ]);
  const results = res.results?.[0];
  const hits = results?.hits || [];

  // Niets in beeld: laat de aanroeper naar de treffers elders toe zoomen.
  const boundsToFit = hits.length ? undefined : (await fetchFilterMeta(client, filter)).bounds;

  return {
    geojson: {
      type: "FeatureCollection",
      features: hits.map(
        ({ geometry, objectID, name }: { geometry: number[]; objectID: string; name: string }) => ({
          type: "Feature",
          properties: { id: objectID, name },
          geometry: { type: "Point", coordinates: geometry },
        })
      ),
    },
    names: hits.map((hit: { name?: string }) => hit.name).filter(Boolean),
    total: results?.nbHits ?? hits.length,
    boundsToFit,
  };
}
